import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Settings, Share2, MessageCircle, UserPlus, UserCheck, Edit2, X, Copy, Check, MousePointer, BarChart2, Shield } from "lucide-react";
import { getCurrentUser, getProfileByUsername } from "../APIs/ProfileApi";
import { useProfileRealtime } from "../hooks/useProfileRealtime";
import ProfileTabs from "../components/ProfileInfo/ProfileTabs";
import EditProfileModal from "../components/ProfileInfo/EditProfileModal";
import SettingsDrawer from "./SettingsDrawer";

const Profile = () => {
  const { userId: username } = useParams(); // ✅ URL se username
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);

  // Step 1: Current logged-in user fetch + target profile resolve
  useEffect(() => {
    const init = async () => {
      setInitialLoading(true);
      try {
        const userRes = await getCurrentUser();
        const me = userRes.data;
        setCurrentUser(me);

        if (username && username !== me.username) {
          // ✅ Dusre user ka profile — username se fetch
          const profileRes = await getProfileByUsername(username);
          setProfileId(profileRes.data.id);
        } else {
          // ✅ Apna profile
          setProfileId(me.profile_id);
        }
      } catch (err) {
        console.error("Init error:", err);
      } finally {
        setInitialLoading(false);
      }
    };
    init();
  }, [username]);

  // Step 2: Profile realtime data (follow status, counts, etc)
  const { profile, setProfile, loading: profileLoading, followLoading, handleFollowToggle } =
    useProfileRealtime(profileId);

  const loading = initialLoading || profileLoading;

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-white/30 animate-spin" />
    </div>
  );

  if (!profile) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-3">
        <p className="text-white/30 text-lg">User not found</p>
        <button
          onClick={() => navigate(-1)}
          className="text-white/40 hover:text-white text-sm underline transition"
        >
          Go back
        </button>
      </div>
    );
  }

  // ✅ is_me check — current user ka profile hai ya nahi
  const isMe = currentUser && currentUser.profile_id === profile.id;

  const profileUrl = profile ? `${window.location.origin}/profile/${profile.username}` : "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24 md:pb-8">

      {/* Banner */}
      <div className="relative h-44 w-full">
        <img
          src={profile.banner_url || "/Images/default-banner.svg"}
          alt="banner"
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.background = "#1a1a1a"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70" />
      </div>

      {/* Profile Icon — CENTER */}
      <div className="flex flex-col items-center -mt-12 px-4">
        <div className="w-24 h-24 rounded-full border-4 border-black
          overflow-hidden bg-white/10 z-10">
          <img
            src={profile.avatar_url || "/Images/default-avatar.svg"}
            alt="avatar"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = "/Images/default-avatar.svg"; }}
          />
        </div>

        <h2 className="text-white font-bold text-xl mt-3">
          @{profile.username}
        </h2>
        {profile.full_name && (
          <p className="text-white/40 text-sm mt-0.5">{profile.full_name}</p>
        )}
        {profile.bio && (
          <p className="text-white/60 text-sm mt-1 text-center max-w-xs">
            {profile.bio}
          </p>
        )}

        <div className="mt-3 flex gap-8">
          <div className="text-center">
            <p className="text-white font-bold text-lg">{profile.followers_count || 0}</p>
            <p className="text-white/40 text-xs">Followers</p>
          </div>
          <div className="text-center">
            <p className="text-white font-bold text-lg">{profile.link_clicks_count || 0}</p>
            <p className="text-white/40 text-xs flex items-center gap-1 justify-center">
              impressions
            </p>
          </div>
        </div>

        {/* ✅ Action Buttons — isMe se decide hoga */}
        <div className="flex gap-2 mt-4">
          {isMe ? (
            <>
              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl
                  bg-white/10 border border-white/10 text-white text-sm
                  font-medium hover:bg-white/20 transition whitespace-nowrap"
              >
                <Edit2 className="w-4 h-4 shrink-0" />
                Edit Profile
              </button>
              <button
                onClick={() => setShowShareSheet(true)}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl
                  bg-white/10 border border-white/10 text-white text-sm
                  font-medium hover:bg-white/20 transition whitespace-nowrap"
              >
                <Share2 className="w-4 h-4 shrink-0" />
                Share
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleFollowToggle}
                disabled={followLoading}
                className={`flex items-center gap-1.5 px-6 py-2 rounded-xl
                  text-sm font-medium transition whitespace-nowrap
                  ${followLoading ? "opacity-50 cursor-not-allowed" : ""}
                  ${profile.is_following
                    ? "bg-white/10 border border-white/10 text-white hover:bg-red-500/20 hover:text-red-400"
                    : "bg-white text-black hover:bg-gray-100"
                  }`}
              >
                {followLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : profile.is_following ? (
                  <><UserCheck className="w-4 h-4 shrink-0" /> Following</>
                ) : (
                  <><UserPlus className="w-4 h-4 shrink-0" /> Follow</>
                )}
              </button>
              <button
                onClick={async () => {
                  try {
                    const { createConversation } = await import("../APIs/chatApi");
                    const convo = await createConversation(profile.user?.id);
                    navigate("/chatbox", { state: { openConvo: convo } });
                  } catch (err) {
                    console.error("DM start error:", err);
                  }
                }}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl
                  bg-white/10 border border-white/10 text-white text-sm
                  font-medium hover:bg-white/20 transition whitespace-nowrap"
              >
                <MessageCircle className="w-4 h-4 shrink-0" />
                Message
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between w-full mt-5
          px-1 border-t border-white/10 pt-4">
          <p className="text-white/60 text-sm">
            <span className="text-white font-bold">
              {profile.posts_count || 0}
            </span> Posts
          </p>
          {isMe && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                  bg-white/[0.05] hover:bg-white/[0.10] transition
                  text-white/40 hover:text-white/70 text-xs font-medium"
              >
                <BarChart2 className="w-3.5 h-3.5" />
                Analytics
              </button>
              {currentUser?.is_superuser && (
                <button
                  onClick={() => navigate('/admin-dashboard')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                    bg-white/[0.05] hover:bg-white/[0.10] transition
                    text-white/40 hover:text-white/70 text-xs font-medium"
                >
                  <Shield className="w-3.5 h-3.5" />
                  Admin
                </button>
              )}
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-full hover:bg-white/10 transition"
              >
                <Settings className="w-5 h-5 text-white/40" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs — pass the profile's user, not logged-in user */}
      <ProfileTabs user={{ id: profile.user?.id, username: profile.username }} isMe={isMe} />

      {showEdit && isMe && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEdit(false)}
          onUpdate={(updated) => {
            if (updated) setProfile((prev) => ({ ...prev, ...updated }));
            setShowEdit(false);
          }}
        />
      )}

      {isMe && (
        <SettingsDrawer
          open={showSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* ── Profile Share Sheet ── */}
      {showShareSheet && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]
            flex items-end md:items-center md:justify-center"
          onClick={(e) => e.target === e.currentTarget && setShowShareSheet(false)}
        >
          <div className="bg-[#111] rounded-t-3xl md:rounded-2xl w-full
            md:max-w-[420px] border-t md:border border-white/10
            shadow-2xl animate-slideUp md:animate-none
            max-h-[80vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 py-3">
              <h2 className="text-white font-semibold">Share Profile</h2>
              <button
                onClick={() => setShowShareSheet(false)}
                className="p-1.5 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-5 h-5 text-white/40" />
              </button>
            </div>

            <div className="px-5 pb-8 space-y-2">
              {/* Copy link */}
              <div className="flex items-center gap-2 bg-white/5 border border-white/10
                rounded-xl p-3 mb-1">
                <p className="flex-1 text-sm text-white/40 truncate">{profileUrl}</p>
                <button
                  onClick={handleCopyLink}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                    text-sm font-medium transition-all duration-200 shrink-0
                    ${copied
                      ? "bg-green-500/20 text-green-400 border border-green-500/30"
                      : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                >
                  {copied
                    ? <><Check className="w-4 h-4" /> Copied!</>
                    : <><Copy className="w-4 h-4" /> Copy</>
                  }
                </button>
              </div>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(profileUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl
                  hover:bg-green-500/10 transition-all duration-200"
              >
                <div className="w-9 h-9 rounded-full bg-green-500/20
                  flex items-center justify-center text-lg">
                  💬
                </div>
                <span className="text-white text-sm font-medium">Share on WhatsApp</span>
              </a>

              {/* DM */}
              <button
                onClick={() => {
                  setShowShareSheet(false);
                  navigate("/chatbox");
                }}
                className="flex items-center gap-3 w-full p-3 rounded-xl
                  hover:bg-purple-500/10 transition-all duration-200"
              >
                <div className="w-9 h-9 rounded-full bg-purple-500/20
                  flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-purple-400" />
                </div>
                <span className="text-white text-sm font-medium">Send via DM</span>
              </button>

              {/* Twitter/X */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Check out " + (profile.username || "") + " on PicProfile!")}&url=${encodeURIComponent(profileUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl
                  hover:bg-sky-500/10 transition-all duration-200"
              >
                <div className="w-9 h-9 rounded-full bg-sky-500/20
                  flex items-center justify-center text-lg">
                  𝕏
                </div>
                <span className="text-white text-sm font-medium">Share on X / Twitter</span>
              </a>
            </div>
          </div>

          <style>{`
            .animate-slideUp {
              animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
            }
            @keyframes slideUp {
              from { transform: translateY(100%); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default Profile;